import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Phone, MessageCircle, MapPin, Clock, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES_OPTIONS = [
  "General Psychiatry Consultation",
  "Depression Treatment",
  "Anxiety Treatment",
  "De-addiction / Rehabilitation",
  "Sexual Health Consultation",
  "Neuropsychiatric Assessment",
  "Child & Adolescent Psychiatry",
  "Geriatric Psychiatry",
  "Medication Review",
  "Psychotherapy / Counselling",
  "Other",
];

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", preferred_date: "", preferred_time: "", service: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error("Please fill in your name and phone number.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/appointments`, form);
      setSubmitted(true);
      toast.success("Appointment request submitted successfully!");
      setForm({ name: "", phone: "", email: "", preferred_date: "", preferred_time: "", service: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try calling us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <section className="py-16 sm:py-24 bg-secondary/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-4" data-testid="contact-heading">
            Book an Appointment
          </h1>
          <p className="text-base sm:text-lg text-muted-foreground max-w-2xl leading-relaxed">
            Schedule a confidential consultation with Dr. Ambrish Singal. Fill the form below or contact us directly.
          </p>
        </div>
      </section>

      <section className="py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-8 lg:gap-12">
            {/* Form */}
            <div className="lg:col-span-3">
              <Card className="border">
                <CardContent className="p-6 sm:p-8">
                  {submitted ? (
                    <div className="text-center py-8" data-testid="success-message">
                      <CheckCircle className="w-12 h-12 text-primary mx-auto mb-4" />
                      <h3 className="font-heading text-xl font-semibold mb-2">Appointment Request Received</h3>
                      <p className="text-muted-foreground mb-4">We will contact you shortly to confirm your appointment.</p>
                      <Button onClick={() => setSubmitted(false)} variant="outline" className="rounded-full">
                        Book Another Appointment
                      </Button>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-5" data-testid="appointment-form">
                      <h3 className="font-heading text-xl font-semibold mb-2">Appointment Request</h3>
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="name">Full Name *</Label>
                          <Input id="name" data-testid="input-name" placeholder="Your full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="phone">Phone Number *</Label>
                          <Input id="phone" data-testid="input-phone" type="tel" placeholder="+91-XXXXX XXXXX" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} required />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email">Email (optional)</Label>
                        <Input id="email" data-testid="input-email" type="email" placeholder="your@email.com" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                      </div>
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="date">Preferred Date</Label>
                          <Input id="date" data-testid="input-date" type="date" value={form.preferred_date} onChange={(e) => setForm({ ...form, preferred_date: e.target.value })} />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="time">Preferred Time</Label>
                          <Input id="time" data-testid="input-time" type="time" value={form.preferred_time} onChange={(e) => setForm({ ...form, preferred_time: e.target.value })} />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Service Required</Label>
                        <Select value={form.service} onValueChange={(val) => setForm({ ...form, service: val })}>
                          <SelectTrigger data-testid="select-service">
                            <SelectValue placeholder="Select a service" />
                          </SelectTrigger>
                          <SelectContent>
                            {SERVICES_OPTIONS.map(s => (
                              <SelectItem key={s} value={s}>{s}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="message">Additional Message</Label>
                        <Textarea id="message" data-testid="input-message" placeholder="Briefly describe your concern or any special requirements..." value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={4} />
                      </div>
                      <Button type="submit" className="w-full rounded-full" size="lg" disabled={submitting} data-testid="submit-appointment">
                        {submitting ? "Submitting..." : "Submit Appointment Request"}
                      </Button>
                      <p className="text-xs text-muted-foreground text-center">
                        Your information is kept strictly confidential.
                      </p>
                    </form>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Contact Info */}
            <div className="lg:col-span-2 space-y-6">
              <Card className="border">
                <CardContent className="p-6 space-y-4">
                  <h3 className="font-heading font-semibold text-lg">Direct Contact</h3>
                  <div className="space-y-4">
                    <a href="tel:+919915096633" className="flex items-center gap-3 text-sm hover:text-primary transition-colors" data-testid="contact-phone-1">
                      <Phone className="w-5 h-5 text-primary" />
                      <span>+91-99150 96633</span>
                    </a>
                    <a href="mailto:drambrishsingal@gmail.com" className="flex items-center gap-3 text-sm hover:text-primary transition-colors" data-testid="contact-email">
                      <Phone className="w-5 h-5 text-primary" />
                      <span>drambrishsingal@gmail.com</span>
                    </a>
                    <a
                      href="https://wa.me/919815333660?text=Hi%20Dr.%20Ambrish,%20I%20would%20like%20to%20book%20an%20appointment."
                      target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-3 text-sm hover:text-primary transition-colors"
                      data-testid="contact-whatsapp"
                    >
                      <MessageCircle className="w-5 h-5 text-[#25D366]" />
                      <span>WhatsApp: +91-98153 33660</span>
                    </a>
                  </div>
                </CardContent>
              </Card>

              <Card className="border">
                <CardContent className="p-6 space-y-4">
                  <h3 className="font-heading font-semibold text-lg">Clinic Locations</h3>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <MapPin className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                      <div>
                        <strong className="text-sm">Clinic</strong>
                        <p className="text-sm text-muted-foreground">Opposite Housefed Society 2, 704, HT Towers, Sector 79, Sahibzada Ajit Singh Nagar, Punjab 140308</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border">
                <CardContent className="p-6 space-y-2">
                  <h3 className="font-heading font-semibold text-lg">Appointment Mode</h3>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Clock className="w-4 h-4 text-primary" />
                    <span>Prior appointment only / On-call</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Please book in advance for a confirmed consultation slot.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
